import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatBRL, formatApiError, formatDateTimeBR } from "../lib/api";
import { getClientIp } from "../lib/clientIp";
import { useAuth } from "../lib/auth";
import { useAuthModal } from "../lib/authModal";
import { toast } from "sonner";
import {
  Wallet as WalletIcon, ArrowDown, ArrowUp, Receipt, Sparkle,
  Info, ShoppingCart, ArrowsCounterClockwise, Coins, Plus, QrCode, CreditCard, X, Warning, Copy, CheckCircle, TrendUp
} from "@phosphor-icons/react";
import useFocusRefresh from "../lib/useFocusRefresh";
import usePullToRefresh from "../lib/usePullToRefresh";
import PullIndicator from "../components/PullIndicator";
import AsaasCardForm from "../components/AsaasCardForm";
import useBodyScrollLock from "../lib/useBodyScrollLock";

const SOURCE_LABELS = {
  auto_cancel_owner: { label: "Cancelamento automático (dono não respondeu)", icon: ArrowsCounterClockwise, color: "text-amber-700" },
  owner_refused: { label: "Reserva recusada pelo dono", icon: ArrowsCounterClockwise, color: "text-amber-700" },
  manual_refund: { label: "Reembolso manual", icon: ArrowsCounterClockwise, color: "text-slate-700" },
  admin_adjustment: { label: "Ajuste do suporte", icon: Sparkle, color: "text-purple-700" },
  signup_bonus: { label: "Bônus de boas-vindas", icon: Sparkle, color: "text-emerald-700" },
  booking_use: { label: "Pagamento de locação", icon: ShoppingCart, color: "text-blue-700" },
  wallet_recharge: { label: "Recarga via PIX/Cartão", icon: Plus, color: "text-emerald-700" },
  withdraw: { label: "Saque via PIX", icon: ArrowUp, color: "text-blue-700" },
  withdraw_refund: { label: "Saque devolvido à carteira", icon: ArrowsCounterClockwise, color: "text-emerald-700" },
  booking_owner_payout: { label: "Repasse de locação recebido", icon: Coins, color: "text-emerald-700" },
  anticipation_fee: { label: "Taxa de antecipação de saque", icon: ArrowUp, color: "text-amber-700" },
};

function sourceMeta(s) { return SOURCE_LABELS[s] || { label: s || "Movimento", icon: Receipt, color: "text-slate-700" }; }

// V12.1 — máscaras p/ exibição da chave PIX (CPF/CNPJ/telefone)
function maskDoc(v, isCnpj) {
  const d = (v || "").replace(/\D/g, "");
  if (isCnpj) {
    return d.slice(0, 14)
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }
  return d.slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}
function maskPhoneDisplay(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d)/, "($1) $2-$3");
  return d.replace(/(\d{2})(\d{5})(\d)/, "($1) $2-$3");
}
function formatPixKey(key, type) {
  if (type === "cpf") return maskDoc(key, false);
  if (type === "cnpj") return maskDoc(key, true);
  if (type === "phone") return maskPhoneDisplay(key);
  return key || "";  // email
}
function docKeyLabel(type) {
  return type === "cnpj" ? "CNPJ" : type === "email" ? "E-mail" : type === "phone" ? "Telefone" : "CPF";
}

// V9.1: status de saque humanizado
const WITHDRAW_STATUS = {
  processing: { label: "Processando PIX…", cls: "bg-amber-100 text-amber-800" },
  completed: { label: "PIX enviado ✓", cls: "bg-emerald-100 text-emerald-800" },
  failed: { label: "Não concluído — valor devolvido", cls: "bg-slate-100 text-slate-700" },
};

export default function Wallet() {
  const { user } = useAuth();
  const { setOpen } = useAuthModal();
  const [balance, setBalance] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [pixKey, setPixKey] = useState(null);
  const [history, setHistory] = useState([]);
  const [withdrawMap, setWithdrawMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showRecharge, setShowRecharge] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showPixKey, setShowPixKey] = useState(false);
  const [anticipateTarget, setAnticipateTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [b, h, w, p, pk] = await Promise.all([
        api.get("/wallet/me"),
        api.get("/wallet/history", { params: { limit: 100 } }),
        api.get("/wallet/withdrawals").catch(() => ({ data: [] })),
        api.get("/payouts/balance").catch(() => ({ data: null })),
        api.get("/wallet/pix-key").catch(() => ({ data: null })),
      ]);
      setBalance(b.data);
      setHistory(h.data || []);
      setPayouts(p.data);
      setPixKey(pk.data);
      const map = {};
      (w.data || []).forEach((x) => { map[x.id || x._id] = x; });
      setWithdrawMap(map);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!user) { setLoading(false); return; } load(); }, [user, load]);
  // V8.5.5: foco da aba + polling 45s — saldo/recargas/saques refletem em tempo real
  useFocusRefresh(load, [], { intervalMs: 45_000 });
  const ptr = usePullToRefresh(load);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-10 text-center" data-testid="wallet-guest">
        <WalletIcon size={48} weight="duotone" className="mx-auto text-[#FF6B35]" />
        <div className="font-heading font-black text-2xl mt-3">Minha Carteira</div>
        <p className="text-slate-600 mt-2 mb-6">Entre para ver seu saldo e extrato.</p>
        <button onClick={() => setOpen(true)} className="btn-primary" data-testid="wallet-open-auth">Entrar ou criar conta</button>
      </div>
    );
  }

  const bal = balance?.balance || 0;
  const available = balance?.available ?? bal;
  const locked = balance?.locked || 0;
  const scheduledItems = balance?.scheduled_items || [];

  return (
    <div className="max-w-3xl mx-auto px-4 py-5" data-testid="wallet-page">
      <PullIndicator pulling={ptr.isPulling} progress={ptr.progress} refreshing={ptr.isRefreshing} />
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="font-heading font-black text-2xl">Minha Carteira</h1>
          <p className="text-sm text-slate-500">Saldo, recebimentos PIX, extrato, saque e antecipação — tudo num lugar só.</p>
        </div>
      </div>
      <div className="p-5 rounded-2xl shadow-md mb-4 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #004E98 0%, #FF6B35 140%)", color: "#fff" }} data-testid="wallet-balance-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold opacity-90">
          <WalletIcon size={16} weight="fill" /> Saldo disponível para saque
        </div>
        <div className="font-heading font-black text-4xl mt-2" data-testid="wallet-balance-value">{formatBRL(available)}</div>
        {locked > 0 && (
          <div className="text-[11px] opacity-90 mt-1" data-testid="wallet-locked-hint">
            + {formatBRL(locked)} a liberar (aguardando a liquidação do cartão)
            {balance?.next_release_at && <> · próximo em {formatDateTimeBR(balance.next_release_at)}</>}
          </div>
        )}
        <p className="text-[11px] opacity-90 mt-2 max-w-md">
          Créditos não expiram. Use seu saldo para alugar ferramentas com mais agilidade.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={() => setShowRecharge(true)} className="inline-flex items-center gap-2 bg-white text-[#004E98] px-4 py-2 rounded-full font-bold text-sm shadow hover:shadow-lg transition" data-testid="wallet-recharge-btn">
            <Plus size={16} weight="bold" /> Recarregar saldo
          </button>
          <button onClick={() => setShowWithdraw(true)} className="inline-flex items-center gap-2 bg-white/15 text-white border-2 border-white/40 px-4 py-2 rounded-full font-bold text-sm hover:bg-white/25 transition" data-testid="wallet-withdraw-btn">
            <ArrowUp size={16} weight="bold" /> Sacar via PIX
          </button>
        </div>
        <Coins size={88} weight="duotone" className="absolute -right-3 -bottom-3 opacity-15" />
      </div>

      {/* V9.3.2: Total já ganho alugando (estatística que o usuário gostava) */}
      {payouts?.total_earned > 0 && (
        <div className="aj-card p-4 mb-4 flex items-center gap-3 bg-emerald-50 border-emerald-100" data-testid="wallet-total-earned">
          <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <TrendUp size={22} weight="bold" className="text-emerald-700" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase font-bold tracking-wider text-emerald-700">Total já ganho alugando</div>
            <div className="font-heading font-black text-2xl text-emerald-800 leading-tight">{formatBRL(payouts.total_earned)}</div>
            <div className="text-[11px] text-emerald-700/70">{payouts.total_earned_count} {payouts.total_earned_count === 1 ? "repasse recebido" : "repasses recebidos"} com suas ferramentas 🔧</div>
          </div>
        </div>
      )}

      {/* V9.3: Chave PIX (recebimento/saque) — gerenciada dentro da carteira */}
      <div className="aj-card p-4 mb-4" data-testid="wallet-pixkey-card">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-heading font-bold text-base">
            <QrCode size={18} weight="bold" className="text-[#004E98]" /> Chave PIX para saque
          </div>
          <button onClick={() => setShowPixKey(true)} className="text-xs py-1.5 px-3 rounded-full border-2 border-slate-200 font-bold hover:bg-slate-50" data-testid="wallet-pixkey-edit">
            {pixKey?.validated ? "Alterar" : "Cadastrar"}
          </button>
        </div>
        {pixKey?.validated ? (
          <div className="mt-2 text-sm flex items-center gap-2 text-emerald-700" data-testid="wallet-pixkey-value">
            <CheckCircle size={16} weight="fill" />
            <span className="font-mono text-xs break-all">{formatPixKey(pixKey.pix_key, pixKey.pix_key_type)}</span>
            <span className="text-[10px] uppercase bg-emerald-50 px-1.5 py-0.5 rounded">{docKeyLabel(pixKey.pix_key_type)}</span>
          </div>
        ) : (
          <p className="text-xs text-slate-500 mt-1">Cadastre sua chave PIX (vinculada ao seu cadastro) para poder sacar.</p>
        )}
      </div>

      {/* V10: Informativos do locador (a receber de locações ativas + aluguéis perdidos) */}
      {(payouts?.pending_total > 0 || payouts?.pending > 0 || payouts?.lost_count > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {(payouts?.pending_total > 0 || payouts?.pending > 0) && (
            <div className="aj-card p-3 bg-blue-50 border-blue-100" data-testid="wallet-escrow-info">
              <div className="text-[11px] uppercase font-bold text-blue-700 flex items-center gap-1"><Info size={13} weight="fill" /> A receber (locações ativas)</div>
              <div className="font-heading font-black text-lg text-blue-900 mt-0.5" data-testid="wallet-pending-total">{formatBRL(payouts.pending_total ?? payouts.pending)}</div>
              <div className="mt-1.5 space-y-1">
                {payouts?.pending > 0 && (
                  <div className="flex items-center justify-between text-[11px] text-blue-800" data-testid="wallet-pending-immediate">
                    <span>Na retirada (imediato): <b>{formatBRL(payouts.pending)}</b></span>
                  </div>
                )}
                {payouts?.pending_uncaptured > 0 && (
                  <div className="flex items-center justify-between text-[11px] text-blue-800/80" data-testid="wallet-pending-uncaptured">
                    <span>Conforme recebimento (cartão): <b>{formatBRL(payouts.pending_uncaptured)}</b></span>
                  </div>
                )}
              </div>
              <div className="text-[10px] text-blue-700/70 mt-1">Parte cai na carteira ao confirmar a retirada; valores no cartão liberam conforme a data de recebimento.</div>
            </div>
          )}
          {payouts?.lost_count > 0 && (
            <div className="aj-card p-3 bg-slate-50 border-slate-200" data-testid="wallet-lost-info">
              <div className="text-[11px] uppercase font-bold text-slate-500 flex items-center gap-1"><ArrowsCounterClockwise size={13} weight="bold" /> Aluguéis perdidos</div>
              <div className="font-heading font-black text-lg text-slate-700 mt-0.5">{formatBRL(payouts.lost_value || 0)}</div>
              <div className="text-[10px] text-slate-400">{payouts.lost_count} por falta de resposta. Responda a tempo para não perder.</div>
            </div>
          )}
        </div>
      )}

      {scheduledItems.length > 0 && (
        <div className="aj-card p-4 mb-4" data-testid="wallet-scheduled-section">
          <div className="flex items-center gap-2 font-heading font-bold text-base">
            <ArrowsCounterClockwise size={18} weight="bold" className="text-[#FF6B35]" /> Cronograma de liberação (cartão)
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Recebimentos no cartão liberam para saque na data de liquidação da Asaas. Quer antes? Antecipe pagando uma taxa (R$ 1,00 + taxa Asaas).
          </p>
          <div className="space-y-2 mt-3">
            {scheduledItems.map((it) => (
              <div key={it.booking_id} className="flex items-center justify-between gap-2 bg-slate-50 rounded-xl p-3" data-testid={`wallet-lock-${it.booking_id}`}>
                <div className="min-w-0">
                  <div className="font-bold text-sm text-slate-800">{formatBRL(it.amount)}</div>
                  <div className="text-[11px] text-slate-500">Libera em {formatDateTimeBR(it.available_at)}</div>
                </div>
                <button
                  onClick={() => setAnticipateTarget(it)}
                  className="text-xs py-2 px-3 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#ff8c5f] text-white font-bold inline-flex items-center gap-1 hover:shadow-md whitespace-nowrap"
                  data-testid={`wallet-anticipate-${it.booking_id}`}
                >
                  <Sparkle size={13} weight="fill" /> Antecipar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="font-heading font-bold text-base mb-2 flex items-center gap-2"><Receipt size={18} weight="bold" /> Extrato</h2>
      {loading ? (
        <div className="aj-card p-8 text-center text-sm text-slate-500" data-testid="wallet-loading">Carregando...</div>
      ) : history.length === 0 ? (
        <div className="aj-card p-8 text-center text-sm text-slate-500" data-testid="wallet-empty">Sem movimentações ainda.</div>
      ) : (
        <div className="space-y-2" data-testid="wallet-history">
          {history.map((h) => {
            const meta = sourceMeta(h.source);
            const Icon = meta.icon;
            const isCredit = h.direction === "credit";
            const wd = h.source === "withdraw" && h.withdraw_id ? withdrawMap[h.withdraw_id] : null;
            const wstatus = wd ? WITHDRAW_STATUS[wd.status] : null;
            return (
              <div key={h.id || h._id || `${h.created_at}-${h.amount}`} className="aj-card p-3 flex items-start gap-3" data-testid={`wallet-entry-${h.source}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isCredit ? "bg-emerald-100" : "bg-red-100"}`}>
                  {isCredit ? <ArrowDown size={18} weight="bold" className="text-emerald-700" /> : <ArrowUp size={18} weight="bold" className="text-red-700" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap"><Icon size={13} weight="bold" className={meta.color} /><span className="font-bold text-sm truncate">{meta.label}</span>
                    {wstatus && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${wstatus.cls}`} data-testid={`withdraw-status-${wd.status}`}>{wstatus.label}</span>}
                  </div>
                  {h.description && <div className="text-xs text-slate-600 truncate">{h.description}</div>}
                  <div className="text-[10px] text-slate-400 mt-0.5">{formatDateTimeBR(h.created_at)}</div>
                </div>
                <div className={`text-right font-heading font-black text-base whitespace-nowrap ${isCredit ? "text-emerald-700" : "text-red-700"}`}>{isCredit ? "+" : "−"} {formatBRL(h.amount)}</div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-6 text-center">
        <Link to="/buscar" className="btn-ghost inline-flex" data-testid="wallet-cta-search"><ShoppingCart size={16} weight="bold" /> Usar saldo em uma locação</Link>
      </div>
      {showRecharge && <RechargeModal onClose={() => setShowRecharge(false)} onSuccess={load} />}
      {showWithdraw && <WithdrawModal balance={available} onClose={() => setShowWithdraw(false)} onSuccess={load} />}
      {showPixKey && <PixKeyModal current={pixKey} onClose={() => setShowPixKey(false)} onSuccess={load} />}
      {anticipateTarget && <AnticipateModal lock={anticipateTarget} onClose={() => setAnticipateTarget(null)} onSuccess={load} />}
    </div>
  );
}

// V8.2: Modal de saque via PIX
function WithdrawModal({ balance, onClose, onSuccess }) {
  const [step, setStep] = useState("loading");  // loading | pix_required | confirm | done
  const [pixKey, setPixKey] = useState(null);
  const [userProfile, setUserProfile] = useState(null);  // V8.3: dados do recebedor
  const [amount, setAmount] = useState("50");
  const [quote, setQuote] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pixForm, setPixForm] = useState({ key: "", key_type: "cpf" });
  const [cfg, setCfg] = useState({ withdraw_min: 1, withdraw_max_per_month: 4, withdraw_free_first: 1, withdraw_fee_after_free: 2.50 });
  useBodyScrollLock(true);

  // V8.5.6: carrega config dinâmica do mínimo/máximo/taxa do backend
  useEffect(() => {
    api.get("/wallet/config").then((r) => {
      setCfg(r.data);
      setAmount(String(r.data.withdraw_min || 50));
    }).catch(() => { /* fallback default */ });
  }, []);

  // V8.3: ao trocar tipo de chave, auto-preenche com dado do cadastro
  useEffect(() => {
    if (!userProfile) return;
    const t = pixForm.key_type;
    if (t === "cpf" || t === "cnpj") setPixForm((f) => ({ ...f, key: userProfile.cpf || "" }));
    else if (t === "email") setPixForm((f) => ({ ...f, key: userProfile.email || "" }));
    else if (t === "phone") setPixForm((f) => ({ ...f, key: userProfile.phone || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixForm.key_type, userProfile]);

  useEffect(() => {
    (async () => {
      try {
        const [pixResp, meResp] = await Promise.all([
          api.get("/wallet/pix-key"),
          api.get("/auth/me"),
        ]);
        const isPJ = meResp.data.doc_type === "cnpj";
        setUserProfile({
          name: meResp.data.name,
          email: meResp.data.email,
          phone: meResp.data.phone,
          cpf: meResp.data.cpf,
          doc_type: meResp.data.doc_type,
        });
        if (pixResp.data.validated) {
          setPixKey(pixResp.data);
          setStep("confirm");
        } else {
          setPixForm((f) => ({ ...f, key_type: isPJ ? "cnpj" : "cpf", key: meResp.data.cpf || "" }));
          setStep("pix_required");
        }
      } catch { setStep("pix_required"); }
    })();
  }, []);

  useEffect(() => {
    if (step !== "confirm") { setQuote(null); return; }
    const v = parseFloat(amount);
    if (!v || v < cfg.withdraw_min) { setQuote(null); return; }
    if (v > balance) { setQuote(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/wallet/withdraw/quote`, { params: { amount: v } });
        setQuote(data);
      } catch (e) {
        const det = e.response?.data?.detail;
        if (det) toast.error(formatApiError(det));
        setQuote(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [amount, step, balance, cfg.withdraw_min]);

  const savePixKey = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/pix-key", pixForm);
      setPixKey({ pix_key: data.pix_key, pix_key_type: data.pix_key_type, validated: true });
      setStep("confirm");
      toast.success("Chave PIX cadastrada e validada!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!confirmed) { toast.error("Confirme os dados marcando a caixa abaixo."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/withdraw", { amount: parseFloat(amount), confirmed: true });
      toast.success(`Saque de ${formatBRL(data.net_to_receive)} solicitado! O PIX é enviado automaticamente e costuma cair em poucos minutos.`);
      onSuccess?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="wallet-withdraw-modal">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-scroll-lock-allow="true" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between sticky top-0 bg-white pb-2 border-b border-slate-100">
          <div>
            <div className="font-heading font-black text-lg">Sacar para PIX</div>
            <div className="text-xs text-slate-500">Saldo disponível: <b>{formatBRL(balance)}</b></div>
          </div>
          <button onClick={onClose} className="text-slate-500" data-testid="withdraw-close"><X size={20} weight="bold" /></button>
        </div>

        {step === "loading" && <div className="py-8 text-center text-slate-500">Carregando...</div>}

        {step === "pix_required" && (
          <div className="space-y-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              <Warning size={13} weight="fill" className="inline mr-1" />
              Você só pode sacar para dados vinculados ao seu cadastro (CPF/CNPJ, e-mail ou telefone).
            </div>
            <div>
              <div className="text-[11px] uppercase font-bold text-slate-500 mb-1">Tipo de chave PIX</div>
              <select value={pixForm.key_type} onChange={(e) => setPixForm({ ...pixForm, key_type: e.target.value })} className="aj-input" data-testid="withdraw-pix-type">
                {userProfile?.doc_type === "cnpj"
                  ? <option value="cnpj">CNPJ (do cadastro)</option>
                  : <option value="cpf">CPF (do cadastro)</option>}
                <option value="email">E-mail (do cadastro)</option>
                <option value="phone">Telefone (do cadastro)</option>
              </select>
            </div>
            <div>
              <div className="text-[11px] uppercase font-bold text-slate-500 mb-1">Chave PIX (vinculada ao seu cadastro)</div>
              <input
                value={formatPixKey(pixForm.key, pixForm.key_type)}
                readOnly
                className="aj-input bg-slate-50 text-slate-700 cursor-not-allowed"
                data-testid="withdraw-pix-key-input"
              />
              <div className="text-[10px] text-slate-500 mt-1">
                Esta chave foi preenchida automaticamente pelo seu cadastro. Para alterar, atualize seu Perfil.
              </div>
            </div>
            {userProfile && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[11px] text-blue-900">
                <b>Recebedor:</b> {userProfile.name}<br />
                <b>{docKeyLabel(pixForm.key_type)}:</b> {formatPixKey(pixForm.key, pixForm.key_type)}
              </div>
            )}
            <button onClick={savePixKey} disabled={busy || !pixForm.key} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="withdraw-pix-save">
              {busy ? "Validando..." : "Validar e continuar"}
            </button>
          </div>
        )}

        {step === "confirm" && pixKey?.pix_key && (
          <>
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3 text-xs space-y-1" data-testid="withdraw-pix-info">
              <div className="font-bold text-emerald-900 flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Chave PIX validada</div>
              {userProfile && <div className="flex justify-between"><span className="text-emerald-700">Recebedor:</span><b className="text-emerald-900">{userProfile.name}</b></div>}
              <div className="flex justify-between"><span className="text-emerald-700">Tipo:</span><b className="text-emerald-900">{docKeyLabel(pixKey.pix_key_type)}</b></div>
              <div className="flex justify-between"><span className="text-emerald-700">Chave:</span><b className="text-emerald-900 font-mono text-[10px]">{formatPixKey(pixKey.pix_key, pixKey.pix_key_type)}</b></div>
            </div>
            <div>
              <div className="text-[11px] uppercase font-bold text-slate-500 mb-1">Valor a sacar (R$)</div>
              <input type="number" min={cfg.withdraw_min} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="aj-input text-xl font-bold" data-testid="withdraw-amount" />
              <div className="text-[10px] text-slate-500 mt-1">
                Mínimo {formatBRL(cfg.withdraw_min)} · Máximo {cfg.withdraw_max_per_month} saques/mês · 1º saque grátis · demais {formatBRL(cfg.withdraw_fee_after_free)} de taxa
              </div>
            </div>
            {quote && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-1" data-testid="withdraw-breakdown">
                <div className="font-bold mb-1">Resumo do saque</div>
                <div className="flex justify-between"><span>Saques este mês:</span><b>{quote.withdraws_this_month}/4</b></div>
                <div className="flex justify-between"><span>Valor solicitado:</span><b>{formatBRL(quote.amount)}</b></div>
                <div className="flex justify-between"><span>{quote.fee_disclosure}:</span><b className={quote.is_free ? "text-emerald-700" : "text-amber-700"}>{quote.is_free ? "GRÁTIS" : `- ${formatBRL(quote.fee)}`}</b></div>
                <div className="border-t border-slate-300 pt-1 flex justify-between font-bold text-sm"><span>Você recebe:</span><span className={quote.valid === false ? "text-red-600" : "text-emerald-700"} data-testid="withdraw-net">{formatBRL(quote.net_to_receive)}</span></div>
              </div>
            )}
            {quote && quote.valid === false && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700" data-testid="withdraw-invalid">
                Com a taxa de {formatBRL(quote.fee)}, o valor mínimo para sacar é <b>{formatBRL(quote.min_amount_with_fee)}</b>.
              </div>
            )}
            <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="withdraw-confirm-check">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5" />
              <span>Confirmo que as informações de saque estão corretas e a chave PIX é minha.</span>
            </label>
            <div className="text-[10px] text-slate-500">
              Não pode alterar a chave aqui — para mudar, cadastre uma nova clicando em &quot;Validar denovo&quot;.
            </div>
            <div className="flex gap-2 sticky bottom-0 bg-white pt-2 border-t border-slate-100">
              <button onClick={() => setStep("pix_required")} className="btn-ghost text-xs" data-testid="withdraw-revalidate">Validar denovo</button>
              <button onClick={submit} disabled={busy || !confirmed || !quote || quote.valid === false} className="btn-primary flex-1 justify-center disabled:opacity-50" data-testid="withdraw-submit">
                {busy ? "Processando..." : quote ? `Sacar ${formatBRL(quote.net_to_receive)}` : "Sacar"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RechargeModal({ onClose, onSuccess }) {
  const { user } = useAuth();
  const [amount, setAmount] = useState("50");
  useBodyScrollLock(true);
  const [method, setMethod] = useState("pix");
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [pixResult, setPixResult] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const PRESETS = [25, 50, 100, 200, 500, 1000];

  useEffect(() => {
    const v = parseFloat(amount);
    if (!v || v < 25) { setQuote(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/wallet/recharge/quote`, { params: { amount: v, payment_method: method } });
        setQuote(data);
      } catch { setQuote(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [amount, method]);

  // Polling status quando PIX gerado
  useEffect(() => {
    if (!pixResult?.session_id) return;
    if (pollAttempts >= 30) return;  // 30 * 3s = 90s
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/wallet/recharge/status/${pixResult.session_id}`);
        if (data.credited) {
          toast.success(`Recarga de ${formatBRL(data.amount)} confirmada!`);
          onSuccess?.();
          onClose();
          return;
        }
        setPollAttempts((a) => a + 1);
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearTimeout(t);
  }, [pixResult, pollAttempts, onSuccess, onClose]);

  const submit = async (cardData = null) => {
    const v = parseFloat(amount);
    if (!v || v < 25) { toast.error("Valor mínimo: R$ 25,00"); return; }
    setBusy(true);
    try {
      const body = { amount: v, payment_method: method };
      if (cardData) Object.assign(body, cardData);
      if (method === "card") body.client_ip = await getClientIp();
      const { data } = await api.post("/wallet/recharge", body);
      if (method === "pix" && data.qr_code) {
        setPixResult(data);
      } else if (method === "card") {
        if (data.status === "paid") {
          toast.success(`Recarga de ${formatBRL(data.credit)} aprovada!`);
          onSuccess?.(); onClose();
        } else {
          toast.info("Pagamento em análise — vamos confirmar e atualizar seu saldo.");
          onSuccess?.(); onClose();
        }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const copyPix = () => {
    if (!pixResult?.qr_code) return;
    navigator.clipboard.writeText(pixResult.qr_code);
    toast.success("Código PIX copiado!");
  };

  // Tela PIX gerado
  if (pixResult) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="wallet-pix-modal">
        <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-4 max-h-[95vh] overflow-y-auto" data-scroll-lock-allow="true" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-heading font-black text-lg">Pague com PIX</div>
              <div className="text-xs text-slate-500">Aguardando confirmação automática</div>
            </div>
            <button onClick={onClose} className="text-slate-500" data-testid="wallet-pix-close"><X size={20} weight="bold" /></button>
          </div>
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-3 text-center">
            <div className="text-xs text-emerald-800">Você vai pagar</div>
            <div className="font-heading font-black text-3xl text-emerald-900" data-testid="wallet-pix-amount">{formatBRL(pixResult.total_charged)}</div>
            <div className="text-[11px] text-emerald-700 mt-1">Saldo creditado: <b>{formatBRL(pixResult.credit)}</b></div>
          </div>
          {pixResult.qr_code_base64 && (
            <div className="flex justify-center bg-white p-3 rounded-xl border border-slate-200">
              <img src={`data:image/png;base64,${pixResult.qr_code_base64}`} alt="QR PIX" className="w-56 h-56" data-testid="wallet-pix-qr" />
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Ou copie o código</div>
            <div className="flex gap-2">
              <input readOnly value={pixResult.qr_code || ""} className="aj-input text-[10px] flex-1 font-mono" data-testid="wallet-pix-copypaste" />
              <button onClick={copyPix} className="btn-primary px-3" data-testid="wallet-pix-copy-btn"><Copy size={16} weight="bold" /></button>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[11px] text-blue-900 flex items-start gap-1.5">
            <Info size={12} weight="fill" className="mt-0.5 shrink-0" />
            <span>O saldo é creditado automaticamente após o pagamento. Mantenha essa tela aberta — vamos atualizar sozinhos. Pollings: {pollAttempts}/30.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="wallet-recharge-modal">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto" data-scroll-lock-allow="true" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading font-black text-lg">Recarregar carteira</div>
            <div className="text-xs text-slate-500">Pagamento via PIX ou cartão (Asaas).</div>
          </div>
          <button onClick={onClose} className="text-slate-500" data-testid="wallet-recharge-close"><X size={20} weight="bold" /></button>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Valor a creditar (R$)</div>
          <input type="number" min="25" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="aj-input text-xl font-bold" data-testid="wallet-recharge-amount" />
          <div className="flex flex-wrap gap-2 mt-2">
            {PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => setAmount(String(p))} className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold ${parseFloat(amount) === p ? "border-[#004E98] bg-[#004E98]/5 text-[#004E98]" : "border-slate-200 text-slate-700"}`} data-testid={`wallet-recharge-preset-${p}`}>R$ {p}</button>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">Mínimo: R$ 25,00</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Forma de pagamento</div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setMethod("pix")} className={`p-3 rounded-xl border-2 flex flex-col items-start ${method === "pix" ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`} data-testid="wallet-recharge-method-pix">
              <div className="flex items-center gap-1.5 font-bold text-sm"><QrCode size={16} weight="bold" /> PIX</div>
              <div className="text-[10px] text-slate-500 mt-0.5">QR in-app · taxa 0,99%</div>
            </button>
            <button type="button" onClick={() => setMethod("card")} className={`p-3 rounded-xl border-2 flex flex-col items-start ${method === "card" ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`} data-testid="wallet-recharge-method-card">
              <div className="flex items-center gap-1.5 font-bold text-sm"><CreditCard size={16} weight="bold" /> Cartão</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Taxa 4,99% · com juros do cartão</div>
            </button>
          </div>
        </div>
        {quote && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs space-y-1" data-testid="wallet-recharge-breakdown">
            <div className="flex items-start gap-2"><Warning size={14} weight="fill" className="text-amber-700 mt-0.5 shrink-0" /><div className="font-bold text-amber-900">Resumo do pagamento</div></div>
            <div className="flex justify-between"><span>Crédito que você recebe:</span><b className="text-emerald-700">{formatBRL(quote.credit)}</b></div>
            <div className="flex justify-between"><span>Taxa de processamento ({method === "pix" ? "0,99%" : "4,99%"}):</span><span>+ {formatBRL(quote.mp_fee)}</span></div>
            <div className="flex justify-between"><span>Taxa de serviço AlugaJá:</span><span>+ {formatBRL(quote.platform_fee)}</span></div>
            <div className="border-t border-amber-300 pt-1 flex justify-between font-bold text-sm"><span>Total a pagar:</span><span data-testid="wallet-recharge-total">{formatBRL(quote.total_to_charge)}</span></div>
            <div className="text-[10px] text-amber-800/80 pt-1">Recarregue uma vez e use o saldo em várias locações.</div>
          </div>
        )}
        {method === "card" && quote ? (
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Dados do cartão</div>
            <AsaasCardForm
              amount={quote.total_to_charge}
              submitting={busy}
              onSubmit={(cardData) => submit(cardData)}
            />
          </div>
        ) : (
          <button onClick={() => submit()} disabled={busy || !quote} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="wallet-recharge-submit">
            {busy ? "Processando..." : quote ? `Pagar ${formatBRL(quote.total_to_charge)} via PIX` : `Recarregar`}
          </button>
        )}
      </div>
    </div>
  );
}


// V9.3: Modal para cadastrar/alterar a chave PIX (vinculada ao cadastro), dentro da carteira
function PixKeyModal({ current, onClose, onSuccess }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ key: "", key_type: current?.pix_key_type || "cpf" });
  const [busy, setBusy] = useState(false);
  useBodyScrollLock(true);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setProfile(r.data);
      setForm((f) => ({
        ...f,
        key_type: current?.pix_key_type || (r.data.doc_type === "cnpj" ? "cnpj" : "cpf"),
        key: r.data.cpf || "",
      }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!profile) return;
    const t = form.key_type;
    if (t === "cpf" || t === "cnpj") setForm((f) => ({ ...f, key: profile.cpf || "" }));
    else if (t === "email") setForm((f) => ({ ...f, key: profile.email || "" }));
    else if (t === "phone") setForm((f) => ({ ...f, key: profile.phone || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.key_type, profile]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/pix-key", form);
      toast.success("Chave PIX cadastrada e validada!");
      onSuccess?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="wallet-pixkey-modal">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3" data-scroll-lock-allow="true" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <QrCode size={22} weight="bold" className="text-[#004E98]" />
          <h3 className="font-heading font-black text-lg">Chave PIX para saque</h3>
        </div>
        <p className="text-xs text-slate-500">A chave precisa ser vinculada ao seu cadastro (CPF/CNPJ, e-mail ou telefone) — assim o dinheiro cai na sua conta.</p>
        <div>
          <div className="text-[11px] uppercase font-bold text-slate-500 mb-1">Tipo de chave</div>
          <div className="grid grid-cols-3 gap-2">
            {(profile?.doc_type === "cnpj"
              ? [["cnpj", "CNPJ"], ["email", "E-mail"], ["phone", "Telefone"]]
              : [["cpf", "CPF"], ["email", "E-mail"], ["phone", "Telefone"]]
            ).map(([v, lbl]) => (
              <button key={v} type="button" onClick={() => setForm((f) => ({ ...f, key_type: v }))} className={`p-2 rounded-xl border-2 text-xs font-bold ${form.key_type === v ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`} data-testid={`pixkey-type-${v}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase font-bold text-slate-500 mb-1">Chave (do seu cadastro)</div>
          <input value={formatPixKey(form.key, form.key_type)} readOnly className="aj-input bg-slate-50 font-mono text-sm" data-testid="pixkey-value" />
          <p className="text-[10px] text-slate-400 mt-1">Para usar outra chave, atualize o dado no seu cadastro.</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="btn-ghost" data-testid="pixkey-cancel">Cancelar</button>
          <button onClick={save} disabled={busy || !form.key} className="btn-primary disabled:opacity-50" data-testid="pixkey-save">
            {busy ? "Validando..." : "Salvar e validar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// V9.3: Modal de antecipação de saque (libera o valor antes da liquidação do cartão)
function AnticipateModal({ lock, onClose, onSuccess }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useBodyScrollLock(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/wallet/anticipate/quote", { booking_id: lock.booking_id });
        setQuote(data);
        if (!data.available) setErr(data.detail || "Antecipação indisponível para esta conta no momento.");
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, [lock.booking_id]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/anticipate", { booking_id: lock.booking_id });
      toast.success(data.message || "Valor liberado para saque!");
      onSuccess?.(); onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="wallet-anticipate-modal">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Sparkle size={22} weight="fill" className="text-[#FF6B35]" />
          <h3 className="font-heading font-black text-lg">Antecipar saque</h3>
        </div>
        {loading ? (
          <div className="py-6 text-center text-slate-500">Calculando taxa de antecipação…</div>
        ) : err ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="anticipate-error">{err}</div>
        ) : quote && (
          <>
            <p className="text-sm text-slate-600">
              Receba <b>{formatBRL(quote.amount)}</b> agora, sem esperar até {formatDateTimeBR(quote.original_release_at)}.
            </p>
            <div className="aj-card p-3 bg-slate-50 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-600">Valor a liberar</span><span className="font-semibold">{formatBRL(quote.amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Taxa Asaas (antecipação)</span><span className="text-red-600">- {formatBRL(quote.asaas_fee || 0)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Taxa AlugaJá</span><span className="text-red-600">- {formatBRL(quote.fixed_fee)}</span></div>
              <div className="flex justify-between font-heading font-black text-base border-t border-slate-200 pt-1.5 mt-1"><span>Vai liberar (líquido)</span><span className="text-emerald-700" data-testid="anticipate-net">{formatBRL(quote.net)}</span></div>
            </div>
            <p className="text-[11px] text-slate-500">Após liberar, o valor entra no seu <b>saldo disponível</b> e você pode sacar via PIX.</p>
          </>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="btn-ghost" data-testid="anticipate-cancel">Cancelar</button>
          <button onClick={confirm} disabled={busy || loading || !!err || !quote?.available} className="btn-primary disabled:opacity-50" data-testid="anticipate-confirm">
            {busy ? "Processando..." : "Confirmar antecipação"}
          </button>
        </div>
      </div>
    </div>
  );
}
